import { getFcmToken, requestNotificationPermission, isFirebaseConfigured } from './firebase';

const DEVICE_ID_KEY = 'smarttag_device_id';

export interface DeviceInfo {
  deviceId: string;
  fcmToken: string | null;
  deviceType: 'ios' | 'android' | 'web';
  deviceName: string;
  appVersion: string;
  osVersion: string;
}

/**
 * Generate a unique device ID using crypto API
 */
const generateDeviceId = (): string => {
  // Try to use crypto.randomUUID if available
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback to manual UUID generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Get or create a persistent device ID
 */
export const getDeviceId = (): string => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);

  if (!deviceId) {
    deviceId = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    console.log('New device ID generated:', deviceId);
  }

  return deviceId;
};

/**
 * Clear device ID (useful for testing or logout)
 */
export const clearDeviceId = (): void => {
  localStorage.removeItem(DEVICE_ID_KEY);
};

/**
 * Detect browser/device type
 */
export const getDeviceType = (): 'ios' | 'android' | 'web' => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  }

  if (/android/.test(userAgent)) {
    return 'android';
  }

  return 'web';
};

/**
 * Get browser/device name
 */
export const getDeviceName = (): string => {
  const userAgent = navigator.userAgent;

  // Detect browser
  let browserName = 'Unknown Browser';
  if (userAgent.includes('Firefox')) {
    browserName = 'Firefox';
  } else if (userAgent.includes('SamsungBrowser')) {
    browserName = 'Samsung Browser';
  } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
    browserName = 'Opera';
  } else if (userAgent.includes('Edg')) {
    browserName = 'Edge';
  } else if (userAgent.includes('Chrome')) {
    browserName = 'Chrome';
  } else if (userAgent.includes('Safari')) {
    browserName = 'Safari';
  }

  // Detect OS
  let osName = 'Unknown OS';
  if (userAgent.includes('Windows')) {
    osName = 'Windows';
  } else if (userAgent.includes('Mac')) {
    osName = 'macOS';
  } else if (userAgent.includes('Linux')) {
    osName = 'Linux';
  } else if (userAgent.includes('Android')) {
    osName = 'Android';
  } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    osName = 'iOS';
  }

  return `${browserName} on ${osName}`;
};

/**
 * Get OS version
 */
export const getOsVersion = (): string => {
  const userAgent = navigator.userAgent;
  let osVersion = '';

  // Windows
  const windowsMatch = userAgent.match(/Windows NT (\d+\.\d+)/);
  if (windowsMatch) {
    const versionMap: Record<string, string> = {
      '10.0': 'Windows 10/11',
      '6.3': 'Windows 8.1',
      '6.2': 'Windows 8',
      '6.1': 'Windows 7',
    };
    osVersion = versionMap[windowsMatch[1]] || `Windows NT ${windowsMatch[1]}`;
  }

  // macOS
  const macMatch = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
  if (macMatch) {
    osVersion = `macOS ${macMatch[1].replace(/_/g, '.')}`;
  }

  // iOS
  const iosMatch = userAgent.match(/OS (\d+[._]\d+[._]?\d*) like Mac OS X/);
  if (iosMatch) {
    osVersion = `iOS ${iosMatch[1].replace(/_/g, '.')}`;
  }

  // Android
  const androidMatch = userAgent.match(/Android (\d+\.?\d*\.?\d*)/);
  if (androidMatch) {
    osVersion = `Android ${androidMatch[1]}`;
  }

  return osVersion || navigator.platform || 'Unknown';
};

/**
 * Get app version from environment
 */
export const getAppVersion = (): string => {
  return import.meta.env.VITE_APP_VERSION || '1.0.0';
};

/**
 * Get complete device info for registration
 */
export const getDeviceInfo = async (): Promise<DeviceInfo> => {
  const deviceId = getDeviceId();
  const deviceType = getDeviceType();
  const deviceName = getDeviceName();
  const appVersion = getAppVersion();
  const osVersion = getOsVersion();

  // Try to get FCM token if Firebase is configured
  let fcmToken: string | null = null;

  if (isFirebaseConfigured()) {
    try {
      // Wrap in a promise that catches both sync and async errors
      fcmToken = await new Promise<string | null>((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('FCM token request timed out - continuing without push notifications');
          resolve(null);
        }, 5000);

        requestNotificationPermission()
          .then((token) => {
            clearTimeout(timeout);
            resolve(token);
          })
          .catch((error) => {
            clearTimeout(timeout);
            console.warn('FCM token failed:', error instanceof Error ? error.message : error);
            resolve(null);
          });
      });
    } catch (error) {
      // Don't block login if FCM fails - just log and continue
      console.warn('FCM token not available (push notifications disabled):', error instanceof Error ? error.message : error);
      fcmToken = null;
    }
  } else {
    console.log('Firebase not configured - push notifications disabled');
  }

  return {
    deviceId,
    fcmToken,
    deviceType,
    deviceName,
    appVersion,
    osVersion,
  };
};

/**
 * Get device info without requesting FCM token (for initial checks)
 */
export const getBasicDeviceInfo = (): Omit<DeviceInfo, 'fcmToken'> => {
  return {
    deviceId: getDeviceId(),
    deviceType: getDeviceType(),
    deviceName: getDeviceName(),
    appVersion: getAppVersion(),
    osVersion: getOsVersion(),
  };
};

/**
 * Update FCM token for existing device
 */
export const refreshFcmToken = async (): Promise<string | null> => {
  if (!isFirebaseConfigured()) {
    return null;
  }

  try {
    return await getFcmToken();
  } catch (error) {
    console.error('Failed to refresh FCM token:', error);
    return null;
  }
};

export default {
  getDeviceId,
  clearDeviceId,
  getDeviceType,
  getDeviceName,
  getOsVersion,
  getAppVersion,
  getDeviceInfo,
  getBasicDeviceInfo,
  refreshFcmToken,
};
